import { type MouseEvent, useCallback, useMemo, useState } from 'react';

type UseCarouselNavigationProps = {
    initialIndex: number;
    totalImages: number;
    onImageChange?: (index: number) => void;
};

export const useCarouselNavigation = ({ initialIndex, totalImages, onImageChange }: UseCarouselNavigationProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const handleNavigationClick = useCallback(
        (direction: 1 | -1) => (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const newIndex = currentIndex + direction;

            if (newIndex < 0 || newIndex >= totalImages) return;

            setCurrentIndex(newIndex);
            onImageChange?.(newIndex);
        },
        [currentIndex, totalImages, onImageChange],
    );

    const activeImageIndex = useMemo(() => Math.min(currentIndex, totalImages - 1), [currentIndex, totalImages]);

    const { showControls, showPrevButton, showNextButton } = useMemo(
        () => ({
            showControls: totalImages > 1,
            showPrevButton: totalImages > 1 && activeImageIndex > 0,
            showNextButton: totalImages > 1 && activeImageIndex < totalImages - 1,
        }),
        [totalImages, activeImageIndex],
    );

    return {
        activeImageIndex,
        showControls,
        showPrevButton,
        showNextButton,
        handleNavigationClick,
    };
};
